import { initializeApp, getApp } from 'firebase/app';
import { getAuth, setPersistence, browserLocalPersistence } from 'firebase/auth';
import { initializeFirestore, getFirestore, persistentLocalCache } from 'firebase/firestore';
import { getDatabase } from 'firebase/database';
import { getStorage } from 'firebase/storage';

// Minimal production-only client error reporter
const safeReportError = async (message, context = {}) => {
  try {
    if (process.env.NODE_ENV !== 'production') return;
    const payload = {
      message: String(message || 'unknown_error'),
      context: { location: window?.location?.pathname || '/', ...context },
      ts: Date.now(),
    };
    await fetch('/api/client-logs', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch (_) {}
};

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL
};

// Validate required env vars in production
(() => {
  const requiredKeys = ['apiKey','authDomain','projectId','appId'];
  const missing = requiredKeys.filter((k) => !firebaseConfig[k]);
  if (missing.length > 0) {
    safeReportError('firebase_env_missing', { missing });
    if (process.env.NODE_ENV === 'production') {
      throw new Error('firebase_env_missing');
    }
  }
})();

// Check if Firebase app is already initialized
let app;
try {
  app = initializeApp(firebaseConfig);
} catch (error) {
  // If app already exists, get the existing one
  if (error.code === 'app/duplicate-app') {
    app = getApp();
  } else {
    throw error;
  }
}

// Initialize Firebase Authentication
const auth = getAuth(app);

// Ensure auth state persists across page reloads
try {
  setPersistence(auth, browserLocalPersistence);
} catch (e) {
  // Non-fatal; falls back to default persistence
}

// 🛡️ SECURITY: Initialize Firestore with offline persistence and size limits
let db;
try {
  // Security: Configurable cache size with reasonable defaults
  const parsed = parseInt(process.env.REACT_APP_MAX_CACHE_SIZE_MB, 10);
  const maxCacheSize = Number.isFinite(parsed) 
    ? parsed * 1024 * 1024 
    : 50 * 1024 * 1024; // Default: 50MB (reduced from 100MB)
  
  db = initializeFirestore(app, {
    localCache: persistentLocalCache({
      // Cache size: Configurable with security limits
      cacheSizeBytes: Math.min(maxCacheSize, 100 * 1024 * 1024), // Max 100MB
    }),
  });
  
  // Note: enableIndexedDbPersistence is deprecated, using FirestoreSettings.cache instead
  // Offline persistence is now handled by persistentLocalCache above
} catch (error) {
  // If Firestore is already initialized, get the existing instance
  if (error.message.includes('cache is already specified')) {
    db = getFirestore(app);
  } else {
    throw error;
  }
}

// Initialize Realtime Database
const rtdb = getDatabase(app);

// Initialize Firebase Storage
const storage = getStorage(app);

export { auth, db, rtdb, storage };
export default app; 